import type { EraDef } from '../types';

/**
 * ERA 6 — ANCIENT EMPIRES
 * A disciplined army: it advances in lockstep and sets up siege lines. Dusty gold desert,
 * mosaic-tiled ground, bronze light. Boss: The Colossus, a walking siege engine shielded by
 * its Bronze Sentinels. Palette: warm gold/bronze with a hot amber sun. Enemy bullets: hot
 * magenta-red (contrasts the cyan player bullets). Hazards: burning-pitch orange, left by
 * lingering artillery impacts (the era's hazard — deployed ballistae and the Colossus itself
 * pock the ground with pools you have to read and step around).
 */
const ENEMY_BULLET = 0xff3f5f;
const HAZARD = 0xff7a3f;

export const ERA_EMPIRES: EraDef = {
  index: 5,
  id: 'empires',
  name: 'Ancient Empires',
  subtitle: '300 BC',
  implemented: true,
  palette: {
    sky: 0x2b2410,
    fog: 0x3d3418,
    fogDensity: 0.017,
    ground: 0xc9a95a,
    groundAlt: 0xa8863f,
    ambient: 0xdcb96a,
    sun: 0xffe6a0,
    sunIntensity: 1.5,
    accent: 0xffd24a,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'desert',
    arenaRadius: 70,
    groundStyle: 'tiles',
    props: [
      { visual: { geo: 'box', color: 0xc9a95a, emissive: 0x5a4620, emissiveIntensity: 0.2, scaleY: 3, anim: 'none' }, count: 18, scale: [0.5, 1.2], y: 0 },
      { visual: { geo: 'torus', color: 0xffd24a, emissive: 0xff7a3f, emissiveIntensity: 0.7, anim: 'pulse' }, count: 12, scale: [0.3, 0.7], y: 0.6 },
      { visual: { geo: 'capsule', color: 0xa8863f, scaleY: 1.4, anim: 'none' }, count: 16, scale: [0.5, 1.0], y: 0.3 },
    ],
  },
  enemies: [
    {
      id: 'e6_scout',
      name: 'Chariot Scout',
      archetype: 'swarmer',
      hp: 30,
      speed: 4.4,
      damage: 10,
      radius: 0.42,
      xp: 7,
      visual: { geo: 'capsule', color: 0xdcb96a, emissive: 0x5a4620, emissiveIntensity: 0.3, scale: 1, anim: 'bob' },
      // Filler: runs straight at you. Counterplay: cheap single kills,
      // just don't let them pile up contact hits while you're dealing with the rest of the line.
      behavior: { type: 'chase' },
    },
    {
      id: 'e6_legionary',
      name: 'Legionary',
      archetype: 'shooter',
      hp: 75,
      speed: 3.4,
      damage: 13,
      radius: 0.55,
      xp: 14,
      visual: { geo: 'cone', color: 0xb08c40, emissive: 0x4a3a18, emissiveIntensity: 0.4, scale: 1.05, anim: 'none' },
      // Signature: marches in lockstep (advance / hard stop), planting its feet and volleying a wall of
      // arrows on the stop. Counterplay: the stop is readable and telegraphed by the halt itself — use the
      // step phase to close or reposition, then read the gaps in the wall during the pause.
      behavior: { type: 'march', stepTime: 1.3, pauseTime: 1.0, speedMult: 1.0 },
      patterns: ['e6_legion_wall'],
      patternCooldown: 2.2,
      fireRange: 14,
    },
    {
      id: 'e6_archer',
      name: 'Bronze Archer',
      archetype: 'shooter',
      hp: 48,
      speed: 4.0,
      damage: 9,
      radius: 0.5,
      xp: 11,
      visual: { geo: 'cone', color: 0xb8a25a, emissive: 0x4a3a18, emissiveIntensity: 0.4, scale: 0.95, anim: 'wobble' },
      // Filler ranged kiter: holds range and peppers gapped arrow walls. Counterplay: standard kiter — close
      // the distance or slot through the gaps; nothing new here, it's the "hold at range" baseline of the era.
      behavior: { type: 'keepDistance', distance: 10, strafe: 0.25 },
      patterns: ['e6_archer_wall'],
      patternCooldown: 2.4,
      fireRange: 16,
    },
    {
      id: 'e6_ballista',
      name: 'War Ballista',
      archetype: 'tank',
      hp: 95,
      speed: 3.0,
      damage: 12,
      radius: 0.65,
      xp: 18,
      shardChance: 0.12,
      visual: { geo: 'box', color: 0x8a6a30, emissive: 0x4a3010, emissiveIntensity: 0.4, scale: 1.3, scaleY: 0.8, anim: 'none' },
      // Signature: marches into range, then plants (a flash telegraphs it) and roots as a turret lobbing
      // heavy pitch shots that leave burning pools. Counterplay: burst it during the plant, or break line of
      // sight/walk past `undeployRange` to force it to pack up and re-march.
      behavior: { type: 'deploy', range: 12, deployTime: 1.3, undeployRange: 20 },
      patterns: ['e6_ballista_shot'],
      patternCooldown: 2.4,
      fireRange: 24,
    },
    {
      id: 'e6_sentinel',
      name: 'Bronze Sentinel',
      archetype: 'shooter',
      hp: 105,
      speed: 3.2,
      damage: 8,
      radius: 0.55,
      xp: 10,
      shardChance: 0.1,
      visual: { geo: 'octa', color: 0xffd24a, emissive: 0xff7a3f, emissiveIntensity: 0.8, scale: 1, anim: 'spin' },
      // The Colossus's shield adds (also seeds the stage pool in small numbers): marches up, plants, and
      // keeps shooting rather than sitting passive. Counterplay: it's a real turret with real HP — kill it
      // during the plant flash if you can catch it, or bait it out of range before engaging the boss.
      behavior: { type: 'deploy', range: 9, deployTime: 0.9, undeployRange: 15 },
      patterns: ['e6_sentinel_shot'],
      patternCooldown: 1.6,
      fireRange: 18,
    },
    {
      id: 'e6_centurion',
      name: 'Centurion',
      archetype: 'elite',
      hp: 70,
      speed: 3.6,
      damage: 18,
      radius: 0.65,
      xp: 32,
      shardChance: 0.4,
      elite: true,
      visual: { geo: 'cone', color: 0xffb84a, emissive: 0xffb84a, emissiveIntensity: 0.9, scale: 1.3, anim: 'none' },
      // Elite-tier march legionary: same lockstep advance/halt, but the halt unleashes a rippling two-wave
      // volley (aimed fan then a wide wall a beat later) instead of one simple wall. Counterplay: same
      // read-the-halt rhythm as a regular legionary, just give the second, wider wave more room.
      behavior: { type: 'march', stepTime: 1.0, pauseTime: 0.8, speedMult: 1.15 },
      patterns: ['e6_centurion_ripple'],
      patternCooldown: 1.8,
      fireRange: 18,
    },
  ],
  patterns: [
    // ---- enemy patterns ----
    {
      id: 'e6_legion_wall',
      emitters: [
        { shape: 'wall', interval: 1.6, bursts: 2, count: 6, spread: 6, gaps: 2, bullet: { speed: 5, lifetime: 3, radius: 0.26, damage: 13, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'e6_archer_wall',
      emitters: [
        { shape: 'wall', interval: 1.3, bursts: 2, count: 7, spread: 7, gaps: 2, bullet: { speed: 6, lifetime: 3, radius: 0.24, damage: 11, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      // Heavy, slow siege lob that leaves a burning-pitch pool where it lands — era 6's hazard signature.
      id: 'e6_ballista_shot',
      emitters: [
        {
          shape: 'aimed',
          interval: 2.4,
          bursts: 2,
          count: 1,
          bullet: { speed: 4, accel: -0.6, minSpeed: 1.2, lifetime: 3.2, radius: 0.4, damage: 24, visual: 'blob', color: HAZARD, linger: { radius: 1.3, duration: 4, dps: 10, color: HAZARD } },
        },
      ],
    },
    {
      id: 'e6_sentinel_shot',
      emitters: [
        { shape: 'aimed', interval: 1.3, bursts: 3, count: 2, spread: 0.3, bullet: { speed: 7, lifetime: 2.4, radius: 0.2, damage: 12, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      // Rippling two-wave volley: an aimed fan lands first, a wide wall a beat later — staggered via `start`
      // so the volley reads as a sequence, not one instant wall.
      id: 'e6_centurion_ripple',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.9, bursts: 2, count: 4, spread: 0.8, bullet: { speed: 7, lifetime: 2.5, radius: 0.22, damage: 14, visual: 'needle', color: ENEMY_BULLET } },
        { shape: 'wall', start: 0.45, interval: 0.9, bursts: 2, count: 6, spread: 6, gaps: 2, bullet: { speed: 5, lifetime: 3, radius: 0.26, damage: 13, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    // ---- boss patterns ----
    {
      // Phase 1: two staggered wall emitters ripple into one wide volley — the teaching pattern.
      id: 'e6_boss_wall_volley',
      emitters: [
        { shape: 'wall', start: 0, interval: 2.4, bursts: 2, count: 9, spread: 9, gaps: 3, bullet: { speed: 5, lifetime: 3.2, radius: 0.28, damage: 15, visual: 'needle', color: ENEMY_BULLET } },
        { shape: 'wall', start: 0.5, interval: 2.4, bursts: 2, count: 9, spread: 9, gaps: 3, angle: 0.12, bullet: { speed: 5, lifetime: 3.2, radius: 0.28, damage: 15, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'e6_boss_aimed_volley',
      emitters: [
        { shape: 'aimed', interval: 1.8, bursts: 3, count: 5, spread: 0.9, bullet: { speed: 6, lifetime: 3, radius: 0.24, damage: 15, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      // Phase-1 onEnter: a single wide gapped wall announcing the fight.
      id: 'e6_boss_enter',
      emitters: [
        { shape: 'wall', interval: 1, bursts: 1, count: 17, spread: 14, gaps: 3, bullet: { speed: 5, lifetime: 3, radius: 0.28, damage: 14, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      // Phase 2: sparse single siege shots with a burning-pitch linger — deliberately thin while shielded so
      // the shield/sentinel hunt is the actual threat, not the boss's own fire.
      id: 'e6_boss_siege',
      emitters: [
        { shape: 'aimed', interval: 3.2, bursts: 3, count: 1, bullet: { speed: 4, accel: -0.5, minSpeed: 1, lifetime: 3.6, radius: 0.45, damage: 28, visual: 'blob', color: HAZARD, linger: { radius: 1.6, duration: 4.5, dps: 11, color: HAZARD } } },
      ],
    },
    {
      // Phase 2 secondary: a handful of lobbed pitch shots landing in random spots, thin and readable.
      id: 'e6_boss_siege_spread',
      emitters: [
        { shape: 'random', interval: 2.6, bursts: 3, count: 2, spread: 6.283, bullet: { speed: 3.5, accel: -0.4, minSpeed: 1, lifetime: 3.4, radius: 0.4, damage: 22, visual: 'blob', color: HAZARD, linger: { radius: 1.3, duration: 4, dps: 9, color: HAZARD } } },
      ],
    },
    {
      // Phase 3: the Colossus, now a planted artillery platform, fires denser siege volleys.
      id: 'e6_boss_siege_heavy',
      emitters: [
        { shape: 'aimed', start: 0, interval: 2.0, bursts: 4, count: 1, bullet: { speed: 4.2, accel: -0.5, minSpeed: 1.2, lifetime: 3.6, radius: 0.46, damage: 30, visual: 'blob', color: HAZARD, linger: { radius: 1.6, duration: 4.5, dps: 12, color: HAZARD } } },
        { shape: 'random', start: 0.6, interval: 2.0, bursts: 4, count: 2, spread: 6.283, bullet: { speed: 3.6, accel: -0.4, minSpeed: 1, lifetime: 3.2, radius: 0.38, damage: 22, visual: 'blob', color: HAZARD, linger: { radius: 1.3, duration: 4, dps: 9, color: HAZARD } } },
      ],
    },
    {
      // Phase-3 onEnter: a dense double wall plus a closing ring — the final, most dangerous phase-change burst.
      id: 'e6_boss_phase3_enter',
      emitters: [
        { shape: 'wall', start: 0, interval: 1, bursts: 1, count: 19, spread: 15, gaps: 3, bullet: { speed: 5.5, lifetime: 3, radius: 0.3, damage: 18, visual: 'needle', color: ENEMY_BULLET } },
        { shape: 'wall', start: 0.35, interval: 1, bursts: 1, count: 19, spread: 15, gaps: 3, angle: 0.15, bullet: { speed: 5.5, lifetime: 3, radius: 0.3, damage: 18, visual: 'needle', color: ENEMY_BULLET } },
        { shape: 'radial', start: 0.7, interval: 1, bursts: 1, count: 16, bullet: { speed: 4, lifetime: 1.4, radius: 0.42, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
  ],
  boss: {
    id: 'e6_colossus',
    name: 'The Colossus',
    title: 'Guardian of the Empire',
    hp: 5200,
    radius: 2.4,
    speed: 2.0,
    damage: 34,
    xp: 480,
    visual: { geo: 'box', color: 0x8a6a30, emissive: 0xff3f5f, emissiveIntensity: 0.5, scale: 1.5, anim: 'none' },
    phases: [
      {
        // Vigil — teaching phase: lockstep march, two readable volleys, opened by a wall-volley onEnter.
        hpBelow: 1,
        name: 'Vigil',
        behavior: { type: 'march', stepTime: 1.8, pauseTime: 1.3, speedMult: 1.0 },
        patterns: ['e6_boss_wall_volley', 'e6_boss_aimed_volley'],
        patternCooldown: 2.0,
        transition: 1.5,
        onEnter: 'e6_boss_enter',
      },
      {
        // Siege — two Bronze Sentinels shield the Colossus; its own fire stays sparse siege lobs so the
        // player can go hunt the sentinels to open it back up.
        hpBelow: 0.65,
        name: 'Siege',
        behavior: { type: 'march', stepTime: 1.5, pauseTime: 1.2, speedMult: 0.9 },
        patterns: ['e6_boss_siege', 'e6_boss_siege_spread'],
        patternCooldown: 3.0,
        special: { type: 'shield', enemy: 'e6_sentinel', count: 2 },
        transition: 2,
      },
      {
        // Wrath of Bronze — fresh pair of sentinels shield it again, and the Colossus itself plants as an
        // artillery platform, opened by a dense phase-change volley.
        hpBelow: 0.3,
        name: 'Wrath of Bronze',
        behavior: { type: 'deploy', range: 9, deployTime: 1.5, undeployRange: 18 },
        patterns: ['e6_boss_siege_heavy', 'e6_boss_wall_volley'],
        patternCooldown: 1.4,
        special: { type: 'shield', enemy: 'e6_sentinel', count: 2 },
        transition: 2,
        onEnter: 'e6_boss_phase3_enter',
      },
    ],
  },
  stages: [
    {
      name: "Legion's March",
      duration: 210,
      objective: { type: 'escort', distance: 22, health: 100, radius: 7 },
      density: { start: 1.0, end: 2.6 },
      pool: [
        { enemy: 'e6_scout', weight: 10 },
        { enemy: 'e6_archer', weight: 4, minTime: 20 },
        { enemy: 'e6_legionary', weight: 4, minTime: 30 },
        { enemy: 'e6_ballista', weight: 2, minTime: 55 },
        { enemy: 'e6_sentinel', weight: 1, minTime: 70 },
      ],
      waves: [
        { t: 4, enemy: 'e6_scout', count: 4, formation: 'ring' },
        { t: 25, enemy: 'e6_archer', count: 3, formation: 'line' },
        { t: 50, enemy: 'e6_scout', count: 16, formation: 'cluster' },
        { t: 75, enemy: 'e6_legionary', count: 3, formation: 'edge' },
        { t: 100, enemy: 'e6_archer', count: 4, formation: 'random' },
        { t: 130, enemy: 'e6_ballista', count: 2, formation: 'edge' },
        { t: 160, enemy: 'e6_scout', count: 20, formation: 'ring' },
        { t: 185, enemy: 'e6_centurion', count: 1, formation: 'edge' },
      ],
    },
    {
      name: 'Siege Lines',
      duration: 240,
      density: { start: 1.4, end: 3.2 },
      pool: [
        { enemy: 'e6_scout', weight: 8 },
        { enemy: 'e6_legionary', weight: 5 },
        { enemy: 'e6_archer', weight: 5 },
        { enemy: 'e6_ballista', weight: 4 },
        { enemy: 'e6_sentinel', weight: 2 },
        { enemy: 'e6_centurion', weight: 2, minTime: 60 },
      ],
      waves: [
        { t: 5, enemy: 'e6_legionary', count: 5, formation: 'ring' },
        { t: 40, enemy: 'e6_ballista', count: 2, formation: 'line' },
        { t: 75, enemy: 'e6_archer', count: 6, formation: 'ring' },
        { t: 105, enemy: 'e6_centurion', count: 1, formation: 'edge' },
        { t: 135, enemy: 'e6_scout', count: 30, formation: 'ring' },
        { t: 165, enemy: 'e6_sentinel', count: 3, formation: 'random' },
        { t: 195, enemy: 'e6_ballista', count: 3, formation: 'cluster' },
        { t: 220, enemy: 'e6_centurion', count: 2, formation: 'line' },
      ],
    },
    {
      name: "The Colossus's Ground",
      duration: 600,
      density: { start: 1.0, end: 1.0 },
      pool: [
        { enemy: 'e6_scout', weight: 6 },
        { enemy: 'e6_archer', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'e6_scout', count: 10, formation: 'ring' }],
      boss: { id: 'e6_colossus', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_legion_plate', weight: 2 },
    { itemId: 'r_sun_disk', weight: 2 },
    { itemId: 'w_bronze_bow', weight: 2 },
    { itemId: 'w_bone_spear', weight: 1 },
    { itemId: 'a_hide', weight: 1 },
    { itemId: 'r_amber', weight: 1 },
  ],
  audio: { music: 'music/era6_empire', ambient: 'amb/desert', sfxSet: 'legion' },
};
